/** @jsxImportSource @emotion/react */
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatMoneyAmount } from "../util";
import { colors } from "./colors";
import { StyledText } from "../assets/typography/typography";
import { effects } from "./effects";
import { css } from "@emotion/react";

/* 
We can use this component to render a responsive line chart. The data you inject must always have a number value for the amount key.
The currency prop is optional and is used to format the y-axis values if they are money amounts. You can also pass the key of the x-axis which
means that the x-axis values can be whatever you like.
*/

export type DataType = {
  [key: string]: string | number;
} & {
  amount: number;
};

type Props = {
  data: DataType[];
  currency?: string;
  xAxisKey: string;
};

const tooltipStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 4px;
  padding: 8px;
  background-color: ${colors.white};
  box-shadow: ${effects.popover};
`;

// The Y-axis is not responsive to the width of the text, so we need to calculate the width of the text and set the width accordingly to not cut the y-axis labels.
// Bug: https://github.com/recharts/recharts/issues/1127
// measureText function: https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/measureText
const getYAxisWidth = (value: DataType[], currency?: string) => {
  const textLengths = value.map((item) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }
    context.font = "12px 'Inter'";
    return context?.measureText(
      currency
        ? formatMoneyAmount(item.amount, currency)
        : item.amount.toString()
    ).width;
  });

  if (textLengths) {
    // + 6 is the width of the tick line which needs to be included
    // @ts-ignore
    return Math.max(...textLengths.filter((item) => item !== undefined)) + 6;
  }
  return undefined;
};

const tickStyle = {
  fontSize: "12px",
  fontFamily: "Inter",
  fill: colors.textPrimary,
};

export const ResponsiveLineChart = ({ data, currency, xAxisKey }: Props) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <XAxis
          dataKey={xAxisKey}
          minTickGap={50}
          tick={tickStyle}
          tickLine={{
            display: "none",
          }}
        />
        <YAxis
          width={getYAxisWidth(data, currency)}
          axisLine={false}
          tick={tickStyle}
          tickLine={{
            stroke: colors.greyBorder,
          }}
          tickFormatter={(value) =>
            currency ? formatMoneyAmount(value, currency) : value.toString()
          }
        />
        <Tooltip
          cursor={{ fill: "rgba(0, 0, 0, 0.08)" }}
          content={(props) => (
            <>
              {props.active && props.payload && props.payload.length && (
                <div css={tooltipStyle}>
                  <StyledText className="bodyTextRegular mb-0">
                    {props.label}
                  </StyledText>
                  <StyledText className="bodyTextSemibold mb-0">
                    {currency
                      ? formatMoneyAmount(
                          props.payload[0].value as number,
                          currency
                        )
                      : props.payload[0].value}
                  </StyledText>
                </div>
              )}
            </>
          )}
        />
        <CartesianGrid
          stroke={colors.greyBorder}
          strokeDasharray="0"
          vertical={false}
        />
        <Line
          type="linear"
          dataKey="amount"
          stroke={colors.blueInteractive}
          strokeWidth={2}
          dot={false}
          activeDot={{ stroke: colors.white, strokeWidth: 3, r: 8 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
